import { IcColorStatusSActive } from '@assets/icons/24x24';
import { Box, For, Grid, GridItem, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import { getMetricValue } from '@components/cockpit/panels/monitoring/monitoring.utils';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { timeDuration } from '@utils/date.utils';
import { getSensorHexColor } from '@utils/sensors/sensors.utils';
import { OtherUnits, TemperatureUnits } from '@utils/unit.utils';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { Coordinates } from '@/types/commons/commons.types';
import { HolodroneAntennaStatus, SensorTypeEnum } from '@/types/sensor/sensors';

import { SensorSection, SensorSectionFactory } from '../../../GlobalTab';
import Section from '../../Section';
import { maintenanceAndCommandSection } from '../MaintenanceAndCommandSection';
import HolodroneSchematics from './HolodroneSchematic';

type HolodroneSectionProps = SensorOrSubSensorInfoByType<SensorTypeEnum.HOLODRONE>;

function HolodroneSoftwares(sensorOrSubSensorInfo: Readonly<HolodroneSectionProps>): SensorSection {
  const situationTime = useAppSelector(selectSituationTime);
  const { configuration, status } = sensorOrSubSensorInfo;

  return [
    <Section
      key="holodrone-softwares"
      operatingValues={
        new Map([
          [
            'holodrone',
            {
              value: configuration.active,
              color: getSensorHexColor(sensorOrSubSensorInfo),
              time: status?.statusChangeDate
                ? timeDuration(status.statusChangeDate, situationTime ?? new Date())
                : undefined,
            },
          ],
        ])
      }
      metricsValues={
        new Map([
          ['temperatureCPU', getMetricValue(TemperatureUnits.CELCIUS, status?.cpuTemp)],
          ['CPUUsage', getMetricValue(OtherUnits.PERCENT, status?.cpuUsage)],
          ['RAMUsage', getMetricValue(OtherUnits.PERCENT, status?.ramUsage)],
          ['disc', getMetricValue(OtherUnits.PERCENT, status?.diskUsage)],
        ])
      }
      informationValues={new Map([['holodrone', status?.version]])}
    />,
    getSensorHexColor(sensorOrSubSensorInfo),
  ];
}

function HolodroneConnectionAndData({ configuration }: Readonly<HolodroneSectionProps>): SensorSection {
  return [
    <Section
      key="connection-section"
      operatingValues={new Map([])}
      metricsValues={new Map([])}
      informationValues={
        new Map<string, string | number | Coordinates | null | undefined>([
          ['position', configuration.sensorPosition],
          ['ip', configuration.host],
          ['port', configuration.port],
        ])
      }
    />,
    null,
  ];
}

function HolodroneAntenna(antennaStatus: Readonly<HolodroneAntennaStatus>) {
  const date = format(new Date(antennaStatus.timestamp), 'dd/MM/yyyy');
  const time = format(new Date(antennaStatus.timestamp), "HH':'mm':'ss");

  return (
    <VStack
      paddingX={9}
      paddingY={4}
      backgroundColor="neutral.700"
      gap={1}
      width="100%"
      height="100%"
      alignItems="start"
    >
      <Text paddingY="11px" fontWeight="medium" color="neutral.200" textTransform="uppercase">
        {antennaStatus.name}
      </Text>
      <HStack paddingY="7px" width="100%" gap={6}>
        <Text color="neutral.300" flexShrink={0}>
          <FormattedMessage id="components.monitoring.sensorDetail.global.holodrone.type" />
        </Text>
        <HStack marginLeft="auto" gap={1}>
          <Text fontWeight="medium" textTransform="uppercase">
            {antennaStatus.type}
          </Text>
          <Icon boxSize="16px" color="green.500" as={IcColorStatusSActive} />
        </HStack>
      </HStack>
      <HStack paddingY="7px" width="100%" gap={6} alignItems="start">
        <Text color="neutral.300" flexShrink={0}>
          <FormattedMessage id="components.monitoring.sensorDetail.global.holodrone.lastReception" />
        </Text>
        <HStack marginLeft="auto" gap={1} alignItems="start">
          <VStack gap="3px" alignItems="end">
            <Text fontWeight="medium">{date}</Text>
            <Text color="neutral.200">{time}</Text>
          </VStack>
          <Icon marginTop="0.75px" boxSize="16px" color="green.500" as={IcColorStatusSActive} />
        </HStack>
      </HStack>
    </VStack>
  );
}

function HolodroneAntennas({ status }: Readonly<HolodroneSectionProps>): SensorSection {
  const antennaStatuses = status?.antennas ?? [];
  if (antennaStatuses.length === 0) {
    return [
      <Box key="holodrone-antennas" width="100%" paddingY={4} paddingX={9} backgroundColor="neutral.700">
        <Text color="neutral.300">
          <FormattedMessage id="components.monitoring.sensorDetail.global.empty" />
        </Text>
      </Box>,
      null,
    ];
  }
  return [
    <Grid
      key="holodrone-antennas"
      width="100%"
      height="min-content"
      gap={0.25}
      backgroundColor="neutral.800"
      templateColumns="repeat(3, 1fr)"
    >
      <For each={antennaStatuses}>{(antennaStatus) => HolodroneAntenna(antennaStatus)}</For>
      {Array.from({ length: (3 - antennaStatuses.length) % 3 }).map(() => (
        <GridItem key={uuidv4()} backgroundColor="neutral.700" />
      ))}
    </Grid>,
    null,
  ];
}

function HolodroneEquipments(): SensorSection {
  return [
    <Box key="holodrone-equipments" width="100%" paddingY={4} paddingX={9} backgroundColor="neutral.700">
      <Text color="neutral.300">
        <FormattedMessage id="components.monitoring.sensorDetail.global.empty" />
      </Text>
    </Box>,
    null,
  ];
}

export const HolodroneSections = new Map<string, SensorSectionFactory<SensorTypeEnum.HOLODRONE>>([
  ['common.schematic', HolodroneSchematics],
  maintenanceAndCommandSection,
  ['holodrone.connexionAndData', HolodroneConnectionAndData],
  ['holodrone.softwares', HolodroneSoftwares],
  ['holodrone.antennas', HolodroneAntennas],
  ['holodrone.equipments', HolodroneEquipments],
]);
